<template>
  <div>
    <b-row v-if="application !== null">
      <b-col cols="12">
        <!-- Payment Details -->
        <b-row class="mt-2 mb-4">
          <b-col cols="12">
            <PRNViewer :tracking-number="application.trackingNumber || application.applicationId || application.trackingNo" />
          </b-col>
        </b-row>

        <b-row class="mx-auto my-2">
          <b-col cols="12 p-0">
            <table class="min-w-full leading-normal mt-1 text-gray" style="border: solid 1px #dedede;">
              <thead>
              <tr class="bg-gray-pale">
                <td colspan="12" class="px-2 py-2">CopyrightAssignmentTransfer Details</td>
              </tr>
              <tr>
                <th class="px-2 py-1">Tracking Number</th>
                <th class="px-2 py-1">Work Title</th>
                <th class="px-2 py-1">Instrument Particulars</th>
                <th class="px-2 py-1">Copyright Number</th>
                <th class="px-2 py-1">Status</th>
              </tr>
              </thead>
              <tbody>
              <tr style="border: solid 1px #dedede;" >
                <td class="px-2 py-1 font-bold text-gray">
                  <span class="">{{ application.trackingNumber || application.applicationId || application.trackingNo }}</span>
                </td>
                <td class="px-2 py-1">
                  <span class="text-gray">{{ (application.originalWork && application.originalWork.title) || application.title || application.newName }}</span>
                </td>
                <td class="px-2 py-1 text-capitalize">
                  <span class="text-gray">{{ formatInstrument(application.particularsInstrument) }}</span>
                </td>
                <td class="px-2 py-1">
                  <span class="text-gray">{{ application.copyrightNumber || 'N/A' }}</span>
                </td>
                <td class="px-2 py-1">
                  <span :class="statusColorCode(application.status)">{{statusDataInfo(application.status)}}</span>
                </td>
              </tr>
              </tbody>
            </table>
          </b-col>
        </b-row>

        <!-- Original Copyright Work Details -->
        <b-row class="mt-2 mb-4" v-if="application.originalWork">
          <b-col cols="12">
            <table class="min-w-full leading-normal mt-1 text-gray"
                   style="border: solid 1px #dedede;">
              <thead>
              <tr class="bg-gray-pale">
                <td colspan="12" class="px-2 py-2 font-bold">Original Copyright Work Details</td>
              </tr>
              <tr>
                <th class="px-2 py-1">Title</th>
                <th class="px-2 py-1">Original Tracking Number</th>
                <th class="px-2 py-1">Status</th>
              </tr>
              </thead>
              <tbody>
              <tr style="border: solid 1px #dedede;">
                <td class="px-2 py-1">{{ application.originalWork.title }}</td>
                <td class="px-2 py-1">{{ application.originalWork.trackingNumber }}</td>
                <td class="px-2 py-1">
                  <span :class="statusColorCode(application.originalWork.status)">{{ statusDataInfo(application.originalWork.status) }}</span>
                </td>
              </tr>
              </tbody>
            </table>
          </b-col>
        </b-row>

        <!-- Work Types -->
        <b-row class="mt-2 mb-4" v-if="(application.originalWork && application.originalWork.workTypes && application.originalWork.workTypes.length > 0) || (application.workTypes && application.workTypes.length > 0)">
          <b-col cols="12">
            <table class="min-w-full leading-normal mt-1 text-gray"
                   style="border: solid 1px #dedede;">
              <thead>
              <tr class="bg-gray-pale">
                <td colspan="12" class="px-2 py-2 font-bold">Work Types</td>
              </tr>
              <tr>
                <th class="px-2 py-1">Name</th>
                <th class="px-2 py-1">Description</th>
                <th class="px-2 py-1">Sub Work Type</th>
                <th class="px-2 py-1">Sub Work Type Description</th>
              </tr>
              </thead>
              <tbody>
              <tr style="border: solid 1px #dedede;" v-for="(wt, idx) in ((application.originalWork && application.originalWork.workTypes) || application.workTypes)" :key="idx">
                <td class="px-2 py-1">{{wt.name}}</td>
                <td class="px-2 py-1">{{wt.description}}</td>
                <td class="px-2 py-1">{{wt.subWorkType ? wt.subWorkType.name : 'N/A'}}</td>
                <td class="px-2 py-1">{{wt.subWorkType ? wt.subWorkType.description : 'N/A'}}</td>
              </tr>
              </tbody>
            </table>
          </b-col>
        </b-row>

        <!-- Assignees Table -->
        <b-row class="mt-2 mb-4" v-if="application.assignee && application.assignee.length > 0">
          <b-col cols="12">
            <table class="min-w-full leading-normal mt-1 text-gray"
                   style="border: solid 1px #dedede;">
              <thead>
              <tr class="bg-gray-pale">
                <td colspan="12" class="px-2 py-2 font-bold">Assignees (Transfer Recipients)</td>
              </tr>
              <tr>
                <th class="px-2 py-1">Name</th>
                <th class="px-2 py-1">Type</th>
                <th class="px-2 py-1">ID / Reg Number</th>
                <th class="px-2 py-1">Email</th>
                <th class="px-2 py-1">Phone</th>
              </tr>
              </thead>
              <tbody>
              <tr style="border: solid 1px #dedede;" v-for="(asg, idx) in application.assignee" :key="idx">
                <td class="px-2 py-1 text-capitalize">
                  {{ asg.assigneeType === 'INDIVIDUAL' 
                      ? [asg.individual.name, asg.individual.middleName, asg.individual.surname].filter(Boolean).join(' ') 
                      : asg.entity.entityName }}
                </td>
                <td class="px-2 py-1 text-capitalize">{{ asg.assigneeType.toLowerCase() }}</td>
                <td class="px-2 py-1">
                  {{ asg.assigneeType === 'INDIVIDUAL' ? asg.individual.idNumber : asg.entity.entityNumber }}
                </td>
                <td class="px-2 py-1">
                  {{ asg.assigneeType === 'INDIVIDUAL' ? asg.individual.email : asg.entity.primaryEmail }}
                </td>
                <td class="px-2 py-1">
                  {{ asg.assigneeType === 'INDIVIDUAL' ? asg.individual.phoneNumber : asg.entity.primaryPhone }}
                </td>
              </tr>
              </tbody>
            </table>
          </b-col>
        </b-row>

        <!-- Rights Being Transferred Table -->
        <b-row class="mt-2 mb-4" v-if="application.rightsBeingTransferred && application.rightsBeingTransferred.length > 0">
          <b-col cols="12">
            <table class="min-w-full leading-normal mt-1 text-gray"
                   style="border: solid 1px #dedede;">
              <thead>
              <tr class="bg-gray-pale">
                <td colspan="12" class="px-2 py-2 font-bold">Rights Being Transferred</td>
              </tr>
              <tr>
                <th class="px-2 py-1">Material / Work Title</th>
                <th class="px-2 py-1">Role / Share Capacity</th>
                <th class="px-2 py-1">Transferor (From)</th>
              </tr>
              </thead>
              <tbody>
              <tr style="border: solid 1px #dedede;" v-for="(right, idx) in application.rightsBeingTransferred" :key="idx">
                <td class="px-2 py-1">{{ right.materialTitle }}</td>
                <td class="px-2 py-1">{{ right.roleName }}</td>
                <td class="px-2 py-1 text-capitalize">
                  {{ right.fromHolderType === 'INDIVIDUAL'
                      ? [right.individual.name, right.individual.middleName, right.individual.surname].filter(Boolean).join(' ')
                      : right.entity.entityName }}
                </td>
              </tr>
              </tbody>
            </table>
          </b-col>
        </b-row>

        <!-- Fallback Ownerships (Unused for new backend) -->
        <b-row class="mt-2 mb-4" v-if="(!application.assignee || application.assignee.length === 0) && (application.ownerships && application.ownerships.length > 0)">
          <b-col cols="12">
            <table class="min-w-full leading-normal mt-1 text-gray"
                   style="border: solid 1px #dedede;">
              <thead>
              <tr class="bg-gray-pale">
                <td colspan="12" class="px-2 py-2 font-bold">Ownerships / Rights Holders</td>
              </tr>
              <tr>
                <th class="px-2 py-1">Name</th>
                <th class="px-2 py-1">Type</th>
                <th class="px-2 py-1">ID Number</th>
                <th class="px-2 py-1">Is Owner</th>
              </tr>
              </thead>
              <tbody>
              <tr style="border: solid 1px #dedede;" v-for="(owner, idx) in application.ownerships" :key="idx">
                <td class="px-2 py-1 text-capitalize">{{owner.holderName}}</td>
                <td class="px-2 py-1 text-capitalize">{{owner.holderType ? owner.holderType.toLowerCase() : ''}}</td>
                <td class="px-2 py-1">{{owner.idNumber}}</td>
                <td class="px-2 py-1">{{owner.isOwner ? 'Yes' : 'No'}}</td>
              </tr>
              </tbody>
            </table>
          </b-col>
        </b-row>

        <!-- Applicant Details -->
        <b-row class="mt-2 mb-4" v-if="application.applicant">
          <b-col cols="12">
            <table class="min-w-full leading-normal mt-1 text-blue-dark"
                   style="border: solid 1px #bdd7f1;">
              <thead>
              <tr class="bg-blue-pale">
                <td colspan="12" class="px-2 py-2 font-bold">Applicant Details</td>
              </tr>
              <tr>
                <th class="px-2 py-1">Name</th>
                <th class="px-2 py-1">Email</th>
                <th class="px-2 py-1">Phone</th>
              </tr>
              </thead>
              <tbody>
              <tr style="border: solid 1px #bdd7f1;">
                <td class="px-2 py-1 text-capitalize">{{ getApplicantName(application.applicant) }}</td>
                <td class="px-2 py-1">{{application.applicant.email}}</td>
                <td class="px-2 py-1">{{application.applicant.phoneNumber}}</td>
              </tr>
              </tbody>
            </table>
          </b-col>
        </b-row>

      </b-col>
    </b-row>
  </div>
</template>
 
<script>
import moment from "moment";
import PRNViewer from "@/views/home/commons/PRNViewer.vue";
 
export default {
  name: "CopyrightAssignmentTransferFormsDetails",
  components: { PRNViewer },
  props:{
    application: {
      type: [Object, null],
      default: null,
    }
  },
  data(){
    return{
      hasComments: false,
      currentRoute: "form",
      selectedId:null,
      margin:"2px",
      hasContent:false,
    }
  },
  methods:{
    getApplicantName(applicant) {
      if (!applicant) return "";
      const first = applicant.firstName || applicant.givenName || "";
      const middle = applicant.middleName || "";
      const last = applicant.lastName || applicant.surname || "";
      return [first, middle, last].filter(Boolean).join(" ");
    },
    formatInstrument(val) {
      if (!val) return "N/A";
      return val.replace(/_/g, " ").toLowerCase();
    },
    isDerivative(val) {
      if (typeof val === 'object' && val !== null) {
        return val.Valid && val.Bool ? 'Yes' : 'No';
      }
      return val ? 'Yes' : 'No';
    },
    getDescription(val) {
      if (typeof val === 'object' && val !== null) {
        return val.Valid ? val.String : '';
      }
      return val || '';
    },
    statusDataInfo(status) {
      if (!status) return "";
      if (status.toUpperCase() === "APPROVED") {
        return "Approved"
      } else if (status.toUpperCase() === "RESERVED") {
        return "Reserved"
      } else if (status.toUpperCase() === "QUERIED") {
        return "Issues Raised"
      } else if (status.toUpperCase() === "ASSIGNED") {
        return "Being Processed"
      } else if (status.toUpperCase() === "PENDING") {
        return "Submitted"
      } else if (status.toUpperCase() === "APPEALED") {
        return "Appealed"
      }else if (status.toUpperCase() === "REJECTED") {
        return "Rejected"
      }else if (status.toUpperCase() === "DRAFT") {
        return "Draft"
      } else if (status.toUpperCase() === "AWAITING_PAYMENT_CONFIRMATION") {
        return "Confirming Payment"
      }else if (status.toUpperCase() === "PAYMENT_FAILED") {
        return "Payment Failed"
      }
      return status;
    },
    statusColorCode(status) {
      if (!status) return "";
      if (status.toUpperCase() === "APPROVED" || status.toUpperCase() === "RESERVED") {
        return "text-green"
      } else if (status.toUpperCase() === "QUERIED") {
        return "text-orange"
      } else if (status.toUpperCase() === "ASSIGNED") {
        return "text-blue"
      } else if (status.toUpperCase() === "PENDING") {
        return "text-gray"
      } else if (status.toUpperCase() === "APPEALED" ) {
        return "text-red"
      }else if (status.toUpperCase() === "REJECTED") {
        return "text-red"
      }else if (status.toUpperCase() === "DRAFT") {
        return "text-gray"
      } else if (status.toUpperCase() === "AWAITING_PAYMENT_CONFIRMATION") {
        return "text-purple"
      }else if (status.toUpperCase() === "PAYMENT_FAILED") {
        return "text-red"
      }
      return "";
    },
    dateFormat(date){
      return moment(date).format("DD-MM-YYYY")
    },
  },
}
</script>

<style scoped>
th, td{
  font-size: 12px;
}
</style>
